import type { ServerResponse } from 'http';
import { hostnameWithoutLang } from './url';

export interface ABTestConfig {
  id: string;
  variants: Record<string, number>;
}

export interface ABTestAnalytics {
  event: string;
  param: string;
}

export interface ABTestEntry extends ABTestConfig {
  analytics: ABTestAnalytics;
}

interface AbCookie {
  name: string;
  value: string;
}

export const COOKIE_PREFIX = 'ab_';
export const AB_COOKIE_MAX_AGE = 30 * 24 * 60 * 60;

export function pickVariantByWeight(variants: Record<string, number>): string {
  const variantEntries = Object.entries(variants);
  const totalWeight = variantEntries.reduce(
    (sum, [, weight]) => sum + weight,
    0
  );
  let random = Math.random() * totalWeight;

  const picked = variantEntries.find(([, weight]) => {
    random -= weight;
    return random <= 0;
  });

  return picked?.[0] ?? variantEntries[variantEntries.length - 1][0];
}

export function getNewAbCookies(
  getCookie: (name: string) => string | undefined,
  tests: readonly ABTestConfig[]
): AbCookie[] {
  return tests
    .filter(test => {
      const existing = getCookie(`${COOKIE_PREFIX}${test.id}`);
      return !existing || !(existing in test.variants);
    })
    .map(test => ({
      name: `${COOKIE_PREFIX}${test.id}`,
      value: pickVariantByWeight(test.variants)
    }));
}

export function injectCookiesIntoHeaders(
  original: Headers,
  cookies: AbCookie[]
): Headers {
  if (cookies.length === 0) return original;

  const headers = new Headers(original);
  let cookieHeader = headers.get('cookie') || '';

  cookies.forEach(({ name, value }) => {
    cookieHeader += `${cookieHeader ? '; ' : ''}${name}=${value}`;
  });
  headers.set('cookie', cookieHeader);

  return headers;
}

export function parseABCookies(cookieString: string): Record<string, string> {
  const result: Record<string, string> = {};

  if (!cookieString) return result;

  cookieString.split(';').forEach(pair => {
    const [rawName, ...rest] = pair.split('=');
    const name = rawName.trim();
    const value = rest.join('=').trim();

    if (name.startsWith(COOKIE_PREFIX) && value) {
      const testId = name.slice(COOKIE_PREFIX.length);
      result[testId] = value;
    }
  });

  return result;
}

export function getABTests(ctx?: { req?: { headers?: { cookie?: string } } }) {
  const cookieString =
    typeof window === 'undefined'
      ? ctx?.req?.headers?.cookie || ''
      : document.cookie;

  return parseABCookies(cookieString);
}

export function getAbCookieDomain(host?: string): string | undefined {
  const env = process.env.NODE_ENV;

  if (env === 'development' || !host) return undefined;

  const cleanHost = host.split(':')[0];

  const pureHost = hostnameWithoutLang(cleanHost);

  return `.${pureHost}`;
}

export function getOrCreateABTests(
  ctx: {
    req?: { headers?: { cookie?: string; host?: string } };
    res?: ServerResponse;
  },
  tests: readonly ABTestConfig[]
): Record<string, string> {
  const cookieString =
    typeof window === 'undefined'
      ? ctx?.req?.headers?.cookie || ''
      : document.cookie;

  const existing = parseABCookies(cookieString);

  const { allVariants, newCookies } = tests.reduce(
    (acc, test) => {
      const valid =
        test.id in acc.allVariants && acc.allVariants[test.id] in test.variants;

      if (valid) return acc;

      const value = pickVariantByWeight(test.variants);

      return {
        allVariants: { ...acc.allVariants, [test.id]: value },
        newCookies: [
          ...acc.newCookies,
          { name: `${COOKIE_PREFIX}${test.id}`, value }
        ]
      };
    },
    { allVariants: existing, newCookies: [] as AbCookie[] }
  );

  if (newCookies.length > 0) {
    const host =
      typeof window === 'undefined'
        ? ctx?.req?.headers?.host
        : window.location.host;
    const domain = getAbCookieDomain(host);
    const domainAttr = domain ? `; Domain=${domain}` : '';

    if (typeof window === 'undefined' && ctx?.res) {
      const setCookieHeaders = newCookies.map(
        ({ name, value }) =>
          `${name}=${value}; Path=/; Max-Age=${AB_COOKIE_MAX_AGE}${domainAttr}`
      );
      const prev = ctx.res.getHeader('Set-Cookie') || [];
      const prevArr = Array.isArray(prev) ? prev : [String(prev)];
      ctx.res.setHeader('Set-Cookie', [...prevArr, ...setCookieHeaders]);
    } else if (typeof window !== 'undefined') {
      newCookies.forEach(({ name, value }) => {
        document.cookie = `${name}=${value}; path=/; max-age=${AB_COOKIE_MAX_AGE}${domainAttr}`;
      });
    }
  }

  return allVariants;
}
